import { Modal as MantineModal } from '@mantine/core'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { theme } from '../../theme'
import { ModalProps, StyledModalProps } from './modal.types'

export const StyledModalContainer = styled(MantineModal)<StyledModalProps>`
  ${({
    theme,
    $position,
    $borderRadiiSize,
    $showModalOverflow,
    $overflowHidden,
    $isFullscreen,
    $width,
    $height,
    $blur,
    $whiteBg,
  }) =>
    css`
      --lemonade-modal-radius: ${theme.borderRadii[$borderRadiiSize]};
      padding: 0px;
      border: none;

      .mantine-Modal-overlay {
        ${$blur && 'backdrop-filter: blur(4px);'}
      }

      .mantine-Modal-body {
        padding: 0;
      }

      .mantine-Modal-inner {
        box-sizing: border-box;
        align-items: center;
        justify-content: center;
      }

      .mantine-Modal-content {
        border-radius: var(--lemonade-modal-radius);
        position: relative;

        ${$whiteBg &&
        `background-color: ${
          $whiteBg ? theme.colors.white : theme.colors.gray80
        };`}
        ${$isFullscreen ? 'width: 100%;' : $width && `width: ${$width}px;`}
        ${$height && `height: ${$height}px;`}
        ${$overflowHidden
          ? 'overflow-y: hidden;'
          : !$isFullscreen && $showModalOverflow
          ? 'overflow-y: visible;'
          : 'overflow-y: auto;'}
        ${$position === 'top-left' &&
        css`
          transform: translate(
            calc(-50vw + 50% + 25px),
            calc(-50vh + 50% + 25px)
          );
        `}
        ${$position === 'top-right' &&
        css`
          transform: translate(
            calc(50vw - 50% - 25px),
            calc(-50vh + 50% + 25px)
          );
        `}
        ${$position === 'bottom-left' &&
        css`
          transform: translate(
            calc(-50vw + 50% + 25px),
            calc(50vh - 50% - 25px)
          );
        `}
        ${$position === 'bottom-right' &&
        css`
          transform: translate(
            calc(50vw - 50% - 25px),
            calc(50vh - 50% - 25px)
          );
        `}
        ${$position === 'top-center-lowered' &&
        css`
          transform: translate(0, calc(-28vh + 50% + 25px));
        `}
      }
    `}
`

const transitionProps = {
  transition: { in: {}, out: {}, transitionProperty: 'all' },
  duration: 0,
}

export const Modal = ({
  width = 560,
  height,
  open,
  onClose,
  ariaDescribedBy,
  ariaLabelledBy,
  blur = false,
  children,
  isFullscreen = false,
  'data-testid': dataTestId,
  whiteBg = true,
  borderRadiiSize = 'large',
  position = 'center',
  showModalOverflow = true,
  overflowHidden,
  enableFocus = true,
  ...props
}: ModalProps) => {
  const [isModalOpen, setModalOpen] = useState(!!open)
  const modalRef = React.useRef<HTMLDivElement>(null)

  const handleCloseModal = () => {
    onClose?.()

    // An `onClose` consumer owns visibility through `open`, so self-closing here would strand
    // the modal hidden while the parent still thinks it is open — and the effect below cannot
    // recover it, because the prop never changed. Consumers that refuse to close mid-request
    // depend on that. Without `onClose` there is nobody to defer to, so close as before.
    if (!onClose) {
      setModalOpen(false)
    }
  }

  useEffect(() => {
    setModalOpen(!!open)
  }, [open])

  return (
    <StyledModalContainer
      closeOnClickOutside={false}
      ref={modalRef}
      className="modal"
      $showModalOverflow={showModalOverflow}
      $overflowHidden={overflowHidden}
      $borderRadiiSize={borderRadiiSize}
      $height={height}
      $width={width}
      $whiteBg={whiteBg}
      $position={position}
      $blur={blur}
      $isFullscreen={isFullscreen}
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
      fullScreen={isFullscreen}
      transitionProps={transitionProps}
      zIndex={theme.zIndex.modal}
      size={width}
      withCloseButton={false}
      data-testid={dataTestId}
      {...props}
      opened={isModalOpen}
      onClose={handleCloseModal}
    >
      <div
        tabIndex={-1}
        style={{ outline: 'none' }}
        {...(enableFocus ? { 'data-autofocus': true } : {})}
      >
        {children}
      </div>
    </StyledModalContainer>
  )
}
